export type LearnCategory = "Foundations" | "Special moves" | "Openings" | "Endgames";
export type LearnFormat = "Interactive lesson" | "3D film" | "Practice";
export type LearnLesson = {
  id: string;
  title: string;
  summary: string;
  category: LearnCategory;
  format: LearnFormat;
  level: "Beginner" | "Intermediate";
  detail: string;
};

export const LEARNING_PATHS = [
  {
    id: "first-moves", title: "Your first moves", subtitle: "A good place to begin.",
    description: "Meet the pieces, protect your king, and learn what makes a winning move.",
    label: "Start from scratch", piece: "n", level: "Beginner",
    lessons: ["/learn/rules", "/learn/special-moves/check-and-checkmate", "/learn/special-moves/castling", "/learn/special-moves/stalemate"],
  },
  {
    id: "opening-ideas", title: "Play with a plan", subtitle: "Make the opening yours.",
    description: "Build a strong centre and discover the ideas behind four classic openings.",
    label: "Build understanding", piece: "b", level: "Intermediate",
    lessons: ["/lessons/italian-game", "/lessons/london-system", "/lessons/queens-gambit-declined", "/lessons/caro-kann-defense"],
  },
  {
    id: "finish-well", title: "Make it count", subtitle: "Finish what you started.",
    description: "Turn an extra piece or a single pawn into a result you can be proud of.",
    label: "Convert your advantage", piece: "r", level: "Intermediate",
    lessons: ["/endgames/queen-mate", "/endgames/rook-mate", "/endgames/square-of-the-pawn", "/endgames/king-and-pawn-opposition"],
  },
] as const;

export type LearningPathId = (typeof LEARNING_PATHS)[number]["id"];
export type LearnProgress = { completed: string[]; saved: string[]; lastVisited: string | null; path: LearningPathId };
export const EMPTY_LEARN_PROGRESS: LearnProgress = { completed: [], saved: [], lastVisited: null, path: "first-moves" };
export const LEARN_STORAGE_KEY = "ponderchess-learning-v1";

// Only internal learning routes can be restored from storage as navigation targets.
export function isLearningRoute(value: unknown): value is string {
  return typeof value === "string" && /^(\/learn\/rules|\/learn\/special-moves\/[a-z0-9-]+|\/lessons\/[a-z0-9-]+|\/endgames\/[a-z0-9-]+)$/.test(value);
}

export function readLearnProgress(value: unknown): LearnProgress {
  const v = value && typeof value === "object" ? value as Record<string, unknown> : {};
  const routes = (items: unknown) => Array.isArray(items) ? [...new Set(items.filter(isLearningRoute))] : [];
  return {
    completed: routes(v.completed), saved: routes(v.saved),
    lastVisited: isLearningRoute(v.lastVisited) ? v.lastVisited : null,
    path: LEARNING_PATHS.find((path) => path.id === v.path)?.id ?? "first-moves",
  };
}

export function nextInPath(path: (typeof LEARNING_PATHS)[number], completed: readonly string[]) {
  return path.lessons.find((id) => !completed.includes(id)) ?? null;
}

export function filterLessons(lessons: LearnLesson[], query: string, category: string, savedOnly: boolean, saved: readonly string[]) {
  const terms = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
  return lessons.filter((lesson) => (category === "All topics" || lesson.category === category)
    && (!savedOnly || saved.includes(lesson.id))
    && terms.every((term) => `${lesson.title} ${lesson.summary} ${lesson.category} ${lesson.format} ${lesson.level}`.toLocaleLowerCase().includes(term)));
}

export function isKnightMove(from: string, to: string) {
  if (!/^[a-h][1-8]$/.test(from) || !/^[a-h][1-8]$/.test(to)) return false;
  const file = Math.abs(from.charCodeAt(0) - to.charCodeAt(0));
  const rank = Math.abs(Number(from[1]) - Number(to[1]));
  return (file === 1 && rank === 2) || (file === 2 && rank === 1);
}
